import React, { FC, useEffect, useState } from 'react';
import OWPdfViewer from '../ow_pdf_viewer/OWPdfViewer';
import ResizableSplitPanel from '../common/ResizableSplitPanel';
import { isPdfFile, isPdfUrl } from '../../utils/ComplianceEvaluationUtils';

interface ComplianceDocumentSplitViewProps {
	// The document to show on the left. Null renders the right pane alone, which is
	// also what happens for a non-PDF — half a screen of "failed to load" helps
	// nobody.
	documentFile?: File;
	// Already-stored documents arrive as a URL rather than bytes — a request's
	// sample, or the document linked to an instance. Ignored when documentFile is
	// given, since that is the copy actually in hand.
	documentUrl?: string;
	fileName: string;
	// The pane beside it: findings, a checklist, or a loading state for either.
	children: React.ReactNode;
	height?: string;
}

const RIGHT_PANE_CLASSES = 'min-h-0 flex-1 overflow-y-auto p-6';

// A compliance document beside whatever the AI made of it, so the two can be read
// against each other instead of in sequence. Used by the supplier's document check
// and the buyer's sample extraction — both are "does this document really say what
// we think it says".
export const ComplianceDocumentSplitView: FC<ComplianceDocumentSplitViewProps> = ({
	documentFile,
	documentUrl,
	fileName,
	children,
	height = '100%',
}): React.ReactElement => {
	// react-pdf is on v4 here and every other caller of OWPdfViewer hands it a URL
	// string, so hand it one too rather than relying on File support this codebase
	// has never exercised. Revoked whenever the file changes or the view closes —
	// the object-URL registry is outside React and leaks without it.
	const [objectUrl, setObjectUrl] = useState<string>(null);
	useEffect(() => {
		if (!documentFile || !isPdfFile(documentFile)) {
			setObjectUrl(null);
			return undefined;
		}
		const createdUrl = URL.createObjectURL(documentFile);
		setObjectUrl(createdUrl);
		return () => URL.revokeObjectURL(createdUrl);
	}, [documentFile]);

	// A stored document is already a URL; only bytes in hand need an object URL.
	// Non-PDFs are filtered out either way — half a screen of "failed to load"
	// helps nobody.
	const previewUrl = objectUrl || (isPdfUrl(documentUrl) ? documentUrl : null);

	if (!previewUrl) {
		return <div className="h-full overflow-y-auto p-6">{children}</div>;
	}

	return (
		<ResizableSplitPanel
			height={height}
			// The right wrapper has no flex growth of its own, so without this the
			// pane is sized by its content and the divider has nothing to resize.
			rightPanelClassName={RIGHT_PANE_CLASSES}
			leftPanel={
				<div className="flex h-full flex-col">
					<OWPdfViewer file={previewUrl} fileName={fileName} />
				</div>
			}
			rightPanel={children}
		/>
	);
};
